import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Breadcrumbs,
  Link,
  Skeleton,
  Divider,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import HomeIcon from '@mui/icons-material/Home';

/**
 * MuiPageHeader - Page header with title, breadcrumb, and action buttons
 *
 * Jakob's Law: Standard page header pattern
 * Law of Proximity: Groups title, breadcrumb, and actions
 * Miller's Law: Limits breadcrumb items to manageable number
 */

const HeaderContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
}));

const HeaderContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: theme.spacing(2),
  flexWrap: 'wrap',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'stretch',
  },
}));

const TitleSection = styled(Box)(({ theme }) => ({
  flex: 1,
  minWidth: 0,
}));

const ActionsSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  flexShrink: 0,
  [theme.breakpoints.down('sm')]: {
    justifyContent: 'flex-end',
    width: '100%',
  },
}));

const StyledBreadcrumbs = styled(Breadcrumbs)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  '& .MuiBreadcrumbs-separator': {
    marginLeft: theme.spacing(0.5),
    marginRight: theme.spacing(0.5),
  },
  '& .MuiBreadcrumbs-li': {
    display: 'flex',
    alignItems: 'center',
  },
}));

const BreadcrumbLink = styled(Link)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  color: theme.palette.text.secondary,
  textDecoration: 'none',
  fontSize: '0.8125rem',
  '&:hover': {
    color: theme.palette.primary.main,
    textDecoration: 'none',
  },
}));

const BreadcrumbText = styled(Typography)(({ theme }) => ({
  fontSize: '0.8125rem',
  color: theme.palette.text.primary,
  fontWeight: 500,
}));

const SubtitleText = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  marginTop: theme.spacing(0.5),
  fontSize: '0.875rem',
}));

const MuiPageHeader = ({
  title,
  subtitle,
  breadcrumbs = [],
  actions,
  showHome = true,
  homeLink = '/',
  loading = false,
  divider = false,
  titleVariant = 'h5',
  maxBreadcrumbs = 4,
  sx = {},
  ...props
}) => {
  // Build breadcrumb items with home
  const breadcrumbItems = React.useMemo(() => {
    const items = [];

    if (showHome) {
      items.push({
        label: 'Home',
        path: homeLink,
        icon: HomeIcon,
      });
    }

    return [...items, ...breadcrumbs];
  }, [breadcrumbs, showHome, homeLink]);

  const renderBreadcrumbs = () => {
    if (breadcrumbItems.length === 0) return null;

    return (
      <StyledBreadcrumbs
        separator={<NavigateNextIcon fontSize="small" sx={{ fontSize: 16 }} />}
        maxItems={maxBreadcrumbs}
        aria-label="breadcrumb"
      >
        {breadcrumbItems.map((item, index) => {
          const isLast = index === breadcrumbItems.length - 1;
          const Icon = item.icon;

          if (loading) {
            return (
              <Skeleton
                key={index}
                variant="text"
                width={60}
                height={20}
              />
            );
          }

          if (isLast || !item.path) {
            return (
              <BreadcrumbText key={index}>
                {Icon && <Icon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />}
                {item.label}
              </BreadcrumbText>
            );
          }

          return (
            <BreadcrumbLink
              key={index}
              component={RouterLink}
              to={item.path}
            >
              {Icon && <Icon sx={{ fontSize: 16 }} />}
              {item.label}
            </BreadcrumbLink>
          );
        })}
      </StyledBreadcrumbs>
    );
  };

  return (
    <HeaderContainer sx={sx} {...props}>
      {renderBreadcrumbs()}
      <HeaderContent>
        <TitleSection>
          {loading ? (
            <>
              <Skeleton variant="text" width={200} height={36} />
              {subtitle && <Skeleton variant="text" width={300} height={20} />}
            </>
          ) : (
            <>
              <Typography
                variant={titleVariant}
                component="h1"
                fontWeight={600}
                sx={{
                  lineHeight: 1.3,
                  wordBreak: 'break-word',
                }}
              >
                {title}
              </Typography>
              {subtitle && (
                <SubtitleText variant="body2">
                  {subtitle}
                </SubtitleText>
              )}
            </>
          )}
        </TitleSection>
        {actions && (
          <ActionsSection>
            {loading ? (
              <Skeleton variant="rectangular" width={100} height={36} sx={{ borderRadius: 1 }} />
            ) : (
              actions
            )}
          </ActionsSection>
        )}
      </HeaderContent>
      {divider && <Divider sx={{ mt: 2 }} />}
    </HeaderContainer>
  );
};

// Helper function to create breadcrumb items from path
MuiPageHeader.createBreadcrumbsFromPath = (pathname, pathLabels = {}) => {
  const segments = pathname.split('/').filter(Boolean);
  const breadcrumbs = [];
  let currentPath = '';

  segments.forEach((segment, index) => {
    currentPath += `/${segment}`;
    const isLast = index === segments.length - 1;

    // Try to get label from pathLabels map, otherwise capitalize segment
    const label = pathLabels[currentPath] ||
                  pathLabels[segment] ||
                  segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' ');

    breadcrumbs.push({
      label,
      path: isLast ? undefined : currentPath,
    });
  });

  return breadcrumbs;
};

MuiPageHeader.propTypes = {
  /** Page title */
  title: PropTypes.node.isRequired,
  /** Page subtitle */
  subtitle: PropTypes.node,
  /** Breadcrumb items */
  breadcrumbs: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      path: PropTypes.string,
      icon: PropTypes.elementType,
    })
  ),
  /** Action buttons */
  actions: PropTypes.node,
  /** Show home link in breadcrumbs */
  showHome: PropTypes.bool,
  /** Home link path */
  homeLink: PropTypes.string,
  /** Loading state */
  loading: PropTypes.bool,
  /** Show divider below header */
  divider: PropTypes.bool,
  /** Title typography variant */
  titleVariant: PropTypes.oneOf(['h4', 'h5', 'h6']),
  /** Maximum breadcrumb items before collapse */
  maxBreadcrumbs: PropTypes.number,
  /** Custom styles */
  sx: PropTypes.object,
};

export default MuiPageHeader;
